/**
 * PaneLayout — renders a tree-based multi-pane layout.
 *
 * Supports horizontal (left-right) and vertical (top-bottom) splits,
 * resizable dividers, and pane focus tracking.
 *
 * Tab drag-and-drop uses @dnd-kit (pointer events) — no HTML5 DnD API.
 */

import { useDroppable } from "@dnd-kit/core";
import {
    horizontalListSortingStrategy,
    SortableContext,
} from "@dnd-kit/sortable";
import { useT } from "@magic-md/i18n/react";
import {
    PanelBottomClose,
    PanelBottomOpen,
    PanelLeftClose,
    PanelLeftOpen,
    PanelRightClose,
    PanelRightOpen,
    PanelTopClose,
    PanelTopOpen,
    X,
} from "lucide-react";
import {
    lazy,
    memo,
    Suspense,
    useCallback,
    useMemo,
    useRef,
    useState,
} from "react";
import { useShallow } from "zustand/react/shallow";
import { cn } from "../lib/utils";
import type { PaneLayout as PaneLayoutType } from "../store/editor-store";
import {
    countExpandedPaneLeaves,
    getPaneLayoutLeafIds,
    useEditorStore,
} from "../store/editor-store";
import type { ContextMenuItem } from "./ContextMenu";
import { ContextMenu, useContextMenu } from "./ContextMenu";
import { buildTabContextMenuItems } from "./context-menu-builders";
import { DocumentPathBreadcrumb } from "./DocumentPathBreadcrumb";
import { EditorPanel } from "./EditorPanel";
import { PaneProvider, usePaneChromeContext } from "./PaneContext";
import { PreviewPanel } from "./preview";
import { RightSidebar } from "./RightSidebar";
import { SplitPane } from "./SplitPane";
import { StoreBackedDraggableTab, TabBarSplitControls } from "./TabBar";
import { type TabBarDropData, useTabDndState } from "./TabDndContext";
import { WorkspaceConfigPanel } from "./WorkspaceConfigPanel";

const DocumentDoctorPanel = lazy(async () => {
    const module = await import("./DocumentDoctorPanel");
    return { default: module.DocumentDoctorPanel };
});

type PaneCollapsePlacement = "left" | "right" | "top" | "bottom";
type PaneCollapseTarget =
    | { kind: "pane"; paneId: string }
    | { kind: "layout"; path: number[] };

function getPaneCollapsePlacement(
    direction: "horizontal" | "vertical",
    index: number,
): PaneCollapsePlacement {
    if (direction === "horizontal") return index === 0 ? "left" : "right";
    return index === 0 ? "top" : "bottom";
}

function isCollapsedLayoutNode(node: PaneLayoutType): boolean {
    return node.collapsed === true;
}

function CollapseIcon({
    placement,
    open,
    size = 12,
}: {
    placement: PaneCollapsePlacement;
    open: boolean;
    size?: number;
}) {
    if (placement === "left") {
        return open ? <PanelLeftOpen size={size} /> : <PanelLeftClose size={size} />;
    }
    if (placement === "right") {
        return open ? <PanelRightOpen size={size} /> : <PanelRightClose size={size} />;
    }
    if (placement === "top") {
        return open ? <PanelTopOpen size={size} /> : <PanelTopClose size={size} />;
    }
    return open ? <PanelBottomOpen size={size} /> : <PanelBottomClose size={size} />;
}

/* ── Resize Handle ── */
export function LayoutResizeHandle({
    direction,
    onResize,
}: {
    direction: "horizontal" | "vertical";
    onResize: (delta: number) => void;
}) {
    const t = useT("editor");
    const startPos = useRef(0);
    // Keep a ref to the latest onResize so the mousemove closure never goes stale
    const onResizeRef = useRef(onResize);
    onResizeRef.current = onResize;

    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            startPos.current = direction === "horizontal" ? e.clientX : e.clientY;

            const handleMouseMove = (me: MouseEvent) => {
                const current = direction === "horizontal" ? me.clientX : me.clientY;
                const delta = current - startPos.current;
                startPos.current = current;
                onResizeRef.current(delta);
            };

            const handleMouseUp = () => {
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);
                document.body.style.cursor = "";
                document.body.style.userSelect = "";
            };

            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
            document.body.style.cursor =
                direction === "horizontal" ? "col-resize" : "row-resize";
            document.body.style.userSelect = "none";
        },
        [direction],
    );

    const isHorizontal = direction === "horizontal";

    return (
        <div
            className={cn(
                "relative z-20 shrink-0 overflow-visible",
                isHorizontal ? "w-0 self-stretch" : "h-0 self-stretch",
            )}
        >
            <div
                role="separator"
                aria-orientation={isHorizontal ? "vertical" : "horizontal"}
                aria-label={t(
                    isHorizontal ? "pane.resizeHorizontal" : "pane.resizeVertical",
                )}
                className={cn(
                    "absolute transition-colors",
                    isHorizontal
                        ? "inset-y-0 left-1/2 w-1 -translate-x-1/2 cursor-col-resize hover:bg-primary/30 active:bg-primary/50"
                        : "inset-x-0 top-1/2 h-1 -translate-y-1/2 cursor-row-resize hover:bg-primary/30 active:bg-primary/50",
                )}
                onMouseDown={handleMouseDown}
            />
        </div>
    );
}

/* ── Per-Pane Tab Bar (droppable via @dnd-kit) ── */
function PaneTabBar({
    paneId,
    isFocused,
    collapsePlacement,
    collapseTarget,
}: {
    paneId: string;
    isFocused: boolean;
    collapsePlacement?: PaneCollapsePlacement;
    collapseTarget?: PaneCollapseTarget;
}) {
    const t = useT("editor");
    const paneTabState = useEditorStore(
        useShallow((s) => {
            const paneState = s.panes.find((p) => p.paneId === paneId);
            const tabIds =
                paneState?.tabIds && paneState.tabIds.length > 0
                    ? paneState.tabIds
                    : paneState?.tabId
                        ? [paneState.tabId]
                        : [];
            return {
                tabId: paneState?.tabId ?? s.activeTabId,
                tabIds,
            };
        }),
    );
    const paneTabId = paneTabState.tabId;
    const paneTabIds = paneTabState.tabIds;
    const tabSignatures = useEditorStore(
        useShallow((s) =>
            paneTabIds.map((tabId) => {
                const tab = s.tabs.find((entry) => entry.id === tabId);
                return `${tabId}\u0000${tab?.title ?? ""}\u0000${tab?.isDirty ? "1" : "0"}`;
            }),
        ),
    );
    const tabs = useMemo(
        () =>
            tabSignatures.map((signature) => {
                const [id, title, dirtyFlag] = signature.split("\u0000");
                return {
                    id,
                    title,
                    isDirty: dirtyFlag === "1",
                };
            }),
        [tabSignatures],
    );
    const setPaneTab = useEditorStore((s) => s.setPaneTab);
    const setFocusedPane = useEditorStore((s) => s.setFocusedPane);
    const splitPane = useEditorStore((s) => s.splitPane);
    const closeTab = useEditorStore((s) => s.closeTab);
    const closePane = useEditorStore((s) => s.closePane);
    const togglePaneCollapsed = useEditorStore((s) => s.togglePaneCollapsed);
    const setPaneLayoutNodeCollapsed = useEditorStore(
        (s) => s.setPaneLayoutNodeCollapsed,
    );
    const paneCount = useEditorStore((s) => s.panes.length);
    const canCollapsePane = useEditorStore((s) => {
        if (!collapsePlacement || !s.paneLayout || s.panes.length <= 1) {
            return false;
        }
        return countExpandedPaneLeaves(s.paneLayout) > 1;
    });
    const dropData: TabBarDropData = { type: "pane-tab-bar", paneId };
    const { setNodeRef, isOver } = useDroppable({
        id: `pane-tab-bar-${paneId}`,
        data: dropData,
    });

    const ctxMenu = useContextMenu();
    const [ctxTabId, setCtxTabId] = useState("");
    const handleTabContextMenu = useCallback(
        (e: React.MouseEvent, tabId: string) => {
            setCtxTabId(tabId);
            ctxMenu.handler(e);
        },
        [ctxMenu],
    );
    const menuItems = ctxTabId
        ? buildTabContextMenuItems({ tabId: ctxTabId, t, closeTab, tabs })
        : [];

    // Sortable IDs must match DraggableTab's useSortable id
    const sortableIds = paneTabIds.map((tid) => `tab-${paneId}-${tid}`);

    if (tabs.length === 0) {
        return (
            <div
                ref={setNodeRef}
                className={cn(
                    "flex items-center h-8 border-b shrink-0",
                    isFocused
                        ? "bg-background border-b-primary/60"
                        : "bg-muted/30 border-b-border",
                    isOver && "bg-primary/10 border-b-primary/50",
                )}
            >
                {isOver && (
                    <span className="text-[10px] text-muted-foreground px-3">
                        {t("pane.dropHere")}
                    </span>
                )}
            </div>
        );
    }

    return (
        <div
            ref={setNodeRef}
            className={cn(
                "flex items-center h-8 border-b overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden shrink-0",
                isFocused
                    ? "bg-background border-b-primary/60"
                    : "bg-muted/30 border-b-border",
                isOver && "bg-primary/10 border-b-primary/50",
            )}
        >
            <SortableContext
                items={sortableIds}
                strategy={horizontalListSortingStrategy}
            >
                {tabs.map((tab) => {
                    const isActive = tab.id === paneTabId;

                    return (
                        <StoreBackedDraggableTab
                            key={tab.id}
                            tabId={tab.id}
                            sourcePaneId={paneId}
                            isActive={isActive}
                            isFocused={isFocused}
                            title={tab.title}
                            isDirty={tab.isDirty}
                            onClick={() => {
                                setFocusedPane(paneId);
                                setPaneTab(paneId, tab.id);
                            }}
                            onClose={() => closeTab(tab.id)}
                            onContextMenu={(e) => handleTabContextMenu(e, tab.id)}
                        />
                    );
                })}
            </SortableContext>

            <div className="flex-1" />
            <TabBarSplitControls
                onSplitRight={() => {
                    setFocusedPane(paneId);
                    splitPane("horizontal");
                }}
                onSplitDown={() => {
                    setFocusedPane(paneId);
                    splitPane("vertical");
                }}
                revealClassName="group-hover/pane:opacity-100"
                testIdPrefix={`pane-tabbar-${paneId}`}
            />
            {/* Close/collapse pane actions only make sense once there is more than one pane. */}
            {paneCount > 1 && (
                <>
                    {canCollapsePane && collapsePlacement && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (collapseTarget?.kind === "layout") {
                                    setPaneLayoutNodeCollapsed(
                                        collapseTarget.path,
                                        true,
                                    );
                                } else {
                                    togglePaneCollapsed(paneId);
                                }
                            }}
                            className="shrink-0 px-2 h-full text-muted-foreground opacity-0 transition-all hover:text-foreground hover:bg-muted/60 group-hover/pane:opacity-100 focus-visible:opacity-100"
                            data-testid={`pane-tabbar-${paneId}-collapse`}
                            title={t("pane.collapse")}
                            aria-label={t("pane.collapse")}
                        >
                            <CollapseIcon placement={collapsePlacement} open={false} />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => closePane(paneId)}
                        className="shrink-0 px-2 h-full text-muted-foreground opacity-0 transition-all hover:text-foreground hover:bg-muted/60 group-hover/pane:opacity-100 focus-visible:opacity-100"
                        title={t("pane.close")}
                        aria-label={t("pane.close")}
                    >
                        <X size={12} />
                    </button>
                </>
            )}
            <ContextMenu {...ctxMenu.props} items={menuItems} />
        </div>
    );
}

/* ── Leaf Pane — renders a single pane with editor+preview ── */
const PaneBody = memo(function PaneBody() {
    const { kind, filePath, sourceFilePath } = usePaneChromeContext();
    const t = useT("editor");
    if (kind === "workspace-config") {
        return <WorkspaceConfigPanel filePath={filePath} />;
    }
    if (kind === "document-doctor") {
        return (
            <Suspense
                fallback={(
                    <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                        {t("common.loading")}
                    </div>
                )}
            >
                <DocumentDoctorPanel documentPath={sourceFilePath ?? filePath} />
            </Suspense>
        );
    }
    return <SplitPane left={<EditorPanel />} right={<PreviewPanel />} />;
});

function PaneLeaf({
    paneId,
    collapsePlacement,
    collapseTarget,
}: {
    paneId: string;
    collapsePlacement?: PaneCollapsePlacement;
    collapseTarget?: PaneCollapseTarget;
}) {
    const isFocused = useEditorStore((s) => s.focusedPaneId === paneId);
    const setFocusedPane = useEditorStore((s) => s.setFocusedPane);
    const dndState = useTabDndState();

    const handleFocus = useCallback(() => {
        if (!isFocused) setFocusedPane(paneId);
    }, [isFocused, paneId, setFocusedPane]);

    return (
        <PaneProvider paneId={paneId}>
            {/* biome-ignore lint: onClick focus tracking */}
            <div
                className="flex flex-col flex-1 min-w-0 min-h-0 relative group/pane"
                onClick={handleFocus}
                onFocus={handleFocus}
            >
                {/* Per-pane tab bar — droppable via dnd-kit */}
                <PaneTabBar
                    paneId={paneId}
                    isFocused={isFocused}
                    collapsePlacement={collapsePlacement}
                    collapseTarget={collapseTarget}
                />
                <DocumentPathBreadcrumb />
                {/* Transparent overlay during tab DnD — prevents CodeMirror from swallowing pointer events */}
                {dndState.activeTabId && (
                    <div className="absolute left-0 right-0 bottom-0 top-8 z-20 pointer-events-none" />
                )}
                <div className="flex flex-1 min-w-0 min-h-0">
                    <PaneBody />
                </div>
            </div>
        </PaneProvider>
    );
}

function CollapsedPaneRail({
    paneId,
    placement,
}: {
    paneId: string;
    placement: PaneCollapsePlacement;
}) {
    const t = useT("editor");
    const railState = useEditorStore(
        useShallow((s) => {
            const pane = s.panes.find((p) => p.paneId === paneId);
            const activeTab = s.tabs.find((tab) => tab.id === pane?.tabId);
            const paneTabs = (pane?.tabIds ?? []).flatMap((tabId) => {
                const tab = s.tabs.find((entry) => entry.id === tabId);
                return tab ? [tab] : [];
            });
            return {
                title: activeTab?.title ?? "Untitled.md",
                isDirty: paneTabs.some((tab) => tab.isDirty),
                tabCount: paneTabs.length,
                tabTitlesText: paneTabs.map((tab) => tab.title).join("\n"),
                isFocused: s.focusedPaneId === paneId,
                paneCount: s.panes.length,
            };
        }),
    );
    const setPaneCollapsed = useEditorStore((s) => s.setPaneCollapsed);
    const closePane = useEditorStore((s) => s.closePane);
    const ctxMenu = useContextMenu();

    const isVerticalRail = placement === "left" || placement === "right";
    const tooltip = railState.tabTitlesText || t("pane.expand");
    const menuItems: ContextMenuItem[] = [
        {
            key: "expand",
            label: t("pane.expand"),
            icon: <CollapseIcon placement={placement} open={true} size={14} />,
            onClick: () => setPaneCollapsed(paneId, false),
        },
        {
            key: "close-pane",
            label: t("pane.close"),
            icon: <X size={14} />,
            disabled: railState.paneCount <= 1,
            onClick: () => closePane(paneId),
        },
    ];

    const expand = useCallback(() => {
        setPaneCollapsed(paneId, false);
    }, [paneId, setPaneCollapsed]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLButtonElement>) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                expand();
            }
        },
        [expand],
    );

    return (
        <>
            <button
                type="button"
                className={cn(
                    "group flex h-full w-full min-w-0 min-h-0 items-center justify-center gap-2 overflow-hidden bg-muted/30 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground",
                    railState.isFocused && "bg-background text-foreground",
                    isVerticalRail ? "flex-col px-1 py-2" : "flex-row px-2 py-1",
                    placement === "left" && "border-r border-border",
                    placement === "right" && "border-l border-border",
                    placement === "top" && "border-b border-border",
                    placement === "bottom" && "border-t border-border",
                )}
                title={tooltip}
                aria-label={t("pane.expandWithTitle", { title: railState.title })}
                onClick={expand}
                onKeyDown={handleKeyDown}
                onContextMenu={ctxMenu.handler}
            >
                <span className="shrink-0">
                    <CollapseIcon placement={placement} open={true} />
                </span>
                <span
                    className={cn(
                        "min-w-0 truncate text-[11px] font-medium",
                        isVerticalRail && "max-h-40",
                    )}
                    style={
                        isVerticalRail
                            ? { writingMode: "vertical-rl", transform: "rotate(180deg)" }
                            : undefined
                    }
                >
                    {railState.title}
                </span>
                <span className="flex shrink-0 items-center gap-1 text-[10px] tabular-nums">
                    {railState.isDirty && (
                        <span
                            className="h-1.5 w-1.5 rounded-full bg-primary"
                            aria-label={t("pane.unsavedChanges")}
                        />
                    )}
                    {railState.tabCount > 1 && <span>{railState.tabCount}</span>}
                </span>
            </button>
            <ContextMenu {...ctxMenu.props} items={menuItems} />
        </>
    );
}

function CollapsedLayoutRail({
    node,
    path,
    placement,
}: {
    node: PaneLayoutType;
    path: number[];
    placement: PaneCollapsePlacement;
}) {
    const t = useT("editor");
    const paneIds = useMemo(
        () => getPaneLayoutLeafIds(node, { includeCollapsed: true }),
        [node],
    );
    const railState = useEditorStore(
        useShallow((s) => {
            const paneTabs = paneIds.flatMap((paneId) => {
                const pane = s.panes.find((entry) => entry.paneId === paneId);
                if (!pane) return [];
                return (pane.tabIds.length > 0 ? pane.tabIds : [pane.tabId])
                    .flatMap((tabId) => {
                        const tab = s.tabs.find((entry) => entry.id === tabId);
                        return tab ? [tab] : [];
                    });
            });
            return {
                paneCount: paneIds.length,
                singleTitle: paneTabs[0]?.title ?? null,
                isDirty: paneTabs.some((tab) => tab.isDirty),
                tabCount: paneTabs.length,
                tabTitlesText: paneTabs.map((tab) => tab.title).join("\n"),
                isFocused: paneIds.includes(s.focusedPaneId),
            };
        }),
    );
    const setPaneLayoutNodeCollapsed = useEditorStore(
        (s) => s.setPaneLayoutNodeCollapsed,
    );
    const ctxMenu = useContextMenu();
    const isVerticalRail = placement === "left" || placement === "right";
    const title = railState.paneCount > 1
        ? t("pane.count", { count: railState.paneCount })
        : railState.singleTitle ?? t("pane.defaultTitle");
    const tooltip = railState.tabTitlesText || t("pane.expandGroup");
    const pathKey = path.join(",");

    const expand = useCallback(() => {
        setPaneLayoutNodeCollapsed(path, false);
    }, [pathKey, path, setPaneLayoutNodeCollapsed]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLButtonElement>) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                expand();
            }
        },
        [expand],
    );

    const menuItems: ContextMenuItem[] = [
        {
            key: "expand",
            label: t("pane.expandGroup"),
            icon: <CollapseIcon placement={placement} open={true} size={14} />,
            onClick: expand,
        },
    ];

    return (
        <>
            <button
                type="button"
                className={cn(
                    "group flex h-full w-full min-w-0 min-h-0 items-center justify-center gap-2 overflow-hidden bg-muted/30 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground",
                    railState.isFocused && "bg-background text-foreground",
                    isVerticalRail ? "flex-col px-1 py-2" : "flex-row px-2 py-1",
                    placement === "left" && "border-r border-border",
                    placement === "right" && "border-l border-border",
                    placement === "top" && "border-b border-border",
                    placement === "bottom" && "border-t border-border",
                )}
                title={tooltip}
                aria-label={t("pane.expandGroupWithTitle", { title })}
                onClick={expand}
                onKeyDown={handleKeyDown}
                onContextMenu={ctxMenu.handler}
            >
                <span className="shrink-0">
                    <CollapseIcon placement={placement} open={true} />
                </span>
                <span
                    className={cn(
                        "min-w-0 truncate text-[11px] font-medium",
                        isVerticalRail && "max-h-40",
                    )}
                    style={
                        isVerticalRail
                            ? { writingMode: "vertical-rl", transform: "rotate(180deg)" }
                            : undefined
                    }
                >
                    {title}
                </span>
                <span className="flex shrink-0 items-center gap-1 text-[10px] tabular-nums">
                    {railState.isDirty && (
                        <span
                            className="h-1.5 w-1.5 rounded-full bg-primary"
                            aria-label={t("pane.unsavedChanges")}
                        />
                    )}
                    {railState.tabCount > 1 && <span>{railState.tabCount}</span>}
                </span>
            </button>
            <ContextMenu {...ctxMenu.props} items={menuItems} />
        </>
    );
}

/* ── Layout Node Renderer ── */
function LayoutNode({
    node,
    path,
    collapsePlacement,
    collapseTarget,
}: {
    node: PaneLayoutType;
    path: number[];
    collapsePlacement?: PaneCollapsePlacement;
    collapseTarget?: PaneCollapseTarget;
}) {
    const setPaneLayoutSizes = useEditorStore((s) => s.setPaneLayoutSizes);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleResize = useCallback(
        (index: number, delta: number) => {
            // Always read the latest layout from the store to avoid stale closures
            const layout = useEditorStore.getState().paneLayout;
            if (!layout) return;

            // Navigate the tree to find the current node at `path`
            let currentNode: PaneLayoutType = layout;
            for (const p of path) {
                if (currentNode.type === "leaf") return;
                currentNode = currentNode.children[p];
            }
            if (currentNode.type === "leaf") return;

            const container = containerRef.current;
            if (!container) return;

            const isHoriz = currentNode.type === "horizontal";
            const containerSize = isHoriz
                ? container.offsetWidth
                : container.offsetHeight;
            if (containerSize <= 0) return;

            const sizes = [...currentNode.sizes];
            const total = sizes.reduce((a, b) => a + b, 0);
            // Accurate pixel → ratio conversion using actual container size
            const ratioDelta = (delta / containerSize) * total;
            sizes[index] = Math.max(10, sizes[index] + ratioDelta);
            sizes[index + 1] = Math.max(10, sizes[index + 1] - ratioDelta);
            // Re-normalize to maintain consistent total
            const newTotal = sizes.reduce((a, b) => a + b, 0);
            const normalized = sizes.map((s) => (s / newTotal) * total);
            setPaneLayoutSizes(path, normalized);
        },
        // path content is stable (same tree position), setPaneLayoutSizes is a Zustand action (stable ref)
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [path.join(","), setPaneLayoutSizes],
    );

    if (node.type === "leaf") {
        if (node.collapsed && collapsePlacement) {
            return (
                <CollapsedPaneRail
                    paneId={node.paneId}
                    placement={collapsePlacement}
                />
            );
        }
        return (
            <PaneLeaf
                paneId={node.paneId}
                collapsePlacement={collapsePlacement}
                collapseTarget={
                    collapsePlacement
                        ? (collapseTarget ?? { kind: "pane", paneId: node.paneId })
                        : undefined
                }
            />
        );
    }

    if (node.collapsed && collapsePlacement) {
        return (
            <CollapsedLayoutRail
                node={node}
                path={path}
                placement={collapsePlacement}
            />
        );
    }

    const isHorizontal = node.type === "horizontal";
    const expandedLeafCount = countExpandedPaneLeaves(node);
    const shouldCollapseAsBranch =
        Boolean(collapsePlacement) && expandedLeafCount === 1;
    const localBranchCollapseTarget: PaneCollapseTarget | undefined =
        shouldCollapseAsBranch ? { kind: "layout", path } : undefined;

    return (
        <div
            ref={containerRef}
            className={`flex ${isHorizontal ? "flex-row" : "flex-col"} flex-1 min-w-0 min-h-0`}
        >
            {node.children.flatMap((child, i) => {
                const childCollapsed = isCollapsedLayoutNode(child);
                const nextChildCollapsed =
                    i < node.children.length - 1
                        ? isCollapsedLayoutNode(node.children[i + 1])
                        : false;
                const childExpandedLeafCount = countExpandedPaneLeaves(child);
                const defaultChildPlacement = getPaneCollapsePlacement(node.type, i);
                const shouldUseBranchCollapse =
                    Boolean(collapsePlacement) &&
                    childExpandedLeafCount > 0 &&
                    Boolean(collapseTarget ?? localBranchCollapseTarget);
                const childPlacement = shouldUseBranchCollapse
                    ? collapsePlacement
                    : defaultChildPlacement;
                const childCollapseTarget = shouldUseBranchCollapse
                    ? (collapseTarget ?? localBranchCollapseTarget)
                    : undefined;
                const items = [
                    <div
                        key={`child-${path.join("-")}-${i}`}
                        className={cn(
                            "flex min-w-0 min-h-0 overflow-hidden",
                            isHorizontal
                                ? i > 0 && !childCollapsed
                                    ? "border-l border-border"
                                    : ""
                                : i > 0 && !childCollapsed
                                    ? "border-t border-border"
                                    : "",
                        )}
                        style={
                            childCollapsed
                                ? { flex: `0 0 ${isHorizontal ? 32 : 30}px` }
                                : { flex: `${node.sizes[i]} 1 0%` }
                        }
                    >
                        <LayoutNode
                            node={child}
                            path={[...path, i]}
                            collapsePlacement={childPlacement}
                            collapseTarget={childCollapseTarget}
                        />
                    </div>,
                ];
                if (
                    i < node.children.length - 1 &&
                    !childCollapsed &&
                    !nextChildCollapsed
                ) {
                    items.push(
                        <LayoutResizeHandle
                            key={`handle-${path.join("-")}-${i}`}
                            direction={node.type}
                            onResize={(delta) => handleResize(i, delta)}
                        />,
                    );
                }
                return items;
            })}
        </div>
    );
}

/* ── Main PaneLayout Container ── */
export function PaneLayoutContainer() {
    const { paneLayout, paneCount, focusedPaneId } = useEditorStore(
        useShallow((s) => ({
            paneLayout: s.paneLayout,
            paneCount: s.panes.length,
            focusedPaneId: s.focusedPaneId,
        })),
    );

    // No layout tree = single-pane mode (backward compatible)
    if (!paneLayout || paneCount === 0) {
        return (
            <PaneProvider paneId="pane-main">
                <div className="flex flex-1 min-w-0 min-h-0">
                    <div className="flex flex-1 flex-col min-w-0 min-h-0">
                        <DocumentPathBreadcrumb />
                        <PaneBody />
                    </div>
                    <RightSidebar />
                </div>
            </PaneProvider>
        );
    }

    return (
        <div className="flex flex-1 min-w-0 min-h-0">
            <LayoutNode node={paneLayout} path={[]} />
            {/* RightSidebar needs PaneContext — scope it to the focused pane */}
            <PaneProvider paneId={focusedPaneId}>
                <RightSidebar />
            </PaneProvider>
        </div>
    );
}
